import { call, cancelled, select } from 'redux-saga/effects';

import { GeneratorResult } from '@pressreader/appstore';
import { error } from '@pressreader/logger';

import { selectCommentsDialogResultCommentsMetadata, selectIsCommentsDialogForArticleIdOpened } from './store/selectors/comments.dialog';
import type { CommentsDialogOptions, CommentsDialogResult, CommentsDialogResultCommentsMetadata } from './types/comments.dialog';
import { loadModule } from './load.module';
import {
    onCommentsDialogClosed,
    onCommentsDialogClosing,
    onCommentsDialogOpened,
    onCommentsDialogOpening,
    onCommentsDialogSagaCancelled,
    openCommentsDialogForArticle,
} from './open.comments.dialog.utils';

export type OpenCommentsDialogAndWaitForResultOptions = CommentsDialogOptions;

export type OpenCommentsDialogAndWaitForResultReturn = {
    commentsMetadata: CommentsDialogResultCommentsMetadata;
} | null; // null is returned in a case the dialog opening has been cancelled by the user or programmatically or there is no data in the store

function* openCommentsDialog(options: CommentsDialogOptions): GeneratorResult<CommentsDialogResult> {
    const { articleId, issueCidDateKey } = options;

    yield call(onCommentsDialogOpening, articleId);

    const popUpSagas = {
        onOpened: onCommentsDialogOpened,
        onClosing: onCommentsDialogClosing,
    };

    const dialogCloseResult: CommentsDialogResult = yield* openCommentsDialogForArticle(
        {
            articleId,
            issueCidDateKey,
        },
        popUpSagas,
    );

    yield call(onCommentsDialogClosed, dialogCloseResult);

    return dialogCloseResult;
}

export function* openCommentsDialogAndGetArticleCommentsMetadata(
    options: CommentsDialogOptions,
): GeneratorResult<OpenCommentsDialogAndWaitForResultReturn> {
    try {
        yield call(loadModule);

        const { articleId, issueCidDateKey } = options;
        const isDialogAlreadyOpenForArticle: ReturnType<typeof selectIsCommentsDialogForArticleIdOpened> = yield select(
            selectIsCommentsDialogForArticleIdOpened,
            { articleId },
        );

        if (isDialogAlreadyOpenForArticle) {
            return null;
        }

        const { cancelled, popupId } = yield* openCommentsDialog({ articleId, issueCidDateKey });

        if (cancelled) {
            return null;
        }

        const commentsMetadataOrNull: ReturnType<typeof selectCommentsDialogResultCommentsMetadata> = yield select(
            selectCommentsDialogResultCommentsMetadata,
            { popupId },
        );

        if (!commentsMetadataOrNull) {
            // the data might've been overridden by another popup
            const err = new Error(`openCommentsDialogAndGetArticleCommentsMetadata: there is no data for popup with id "${popupId}"`);
            error(err);
            throw err;
        }
        return {
            commentsMetadata: commentsMetadataOrNull,
        };
    } finally {
        if (yield cancelled()) {
            yield call(onCommentsDialogSagaCancelled);
        }
    }
}
